import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { Button, Popover, Space, Tag } from "antd";

import "./index.css";
import { getGraphData } from "../../axios/index";
import { userRoles } from "../utils/userRoles";
import {
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  LineChartOutlined,
  SaveFilled,
  SearchOutlined,
} from "@ant-design/icons";
import { JSONFileTypeIcon, XMLFileTypeIcon } from "../../icons/svgComponent";

function ActionButtons(props) {
  const dispatch = useDispatch();
  const urlParams = useParams();
  const location = useLocation();

  const pathParts = location.pathname.split("/").filter(Boolean);
  const pathId = pathParts[pathParts.length - 1];

  const currentId = pathId || urlParams.id;
  const routeId = Number(currentId);
  const isNewRecord = !Number.isInteger(routeId) || routeId <= 0;

  const [isPopover, setIsPopover] = useState(false);
  const userInfo = useSelector((state) => state.userInfo);
  const [userRole, setUserRole] = useState();

  useEffect(() => {
    if (userInfo.sAMAccountName) {
      setUserRole(userRoles(userInfo.Roles));
    }

    dispatch(getGraphData());
  }, [dispatch, userInfo]);

  useEffect(() => {
    setIsPopover(false);
  }, [location.pathname]);

  const handleDeletEvent = (status) => {
    return props.handleDeletEvent(status);
  };

  const handleEvent = (status) => {
    return props.handleEditStatus(status);
  };

  const handleSave = () => {
    return props.handleSaveEvent(true);
  };

  const handleAdvancedSearchEvent = () => {
    return props.handleAdvancedSearchStatus("search", true);
  };

  const handleGraphVisualizer = () => {
    return props.handleAdvancedSearchStatus("graph", true);
  };

  const handlePopoverChange = (event) => {
    setIsPopover(event);
  };

  const handleDownload = (type) => {
    if (isNewRecord) {
      setIsPopover(false);
      window.alert("Please save the SAM before downloading.");
      return;
    }

    const query = new URLSearchParams({
      moduleName: props.moduleName,
      downloadType: type,
      id: String(routeId),
    });

    const downloadUrl = `${
      process.env.REACT_APP_BACKEND_API_URL
    }/api/download/material?${query.toString()}`;

    const win = window.open(downloadUrl, "_blank");

    if (win) {
      win.focus();
    }
  };

  const renderActionButtons = () => {
    if (props.editStatus) {
      return (
        <>
          {props.moduleName === "material" ? (
            <Button
              type="text"
              size="small"
              className="action-btn"
              onClick={handleGraphVisualizer}
            >
              <Space size={4}>
                <LineChartOutlined />
                <span>Visualizer</span>
              </Space>
            </Button>
          ) : null}

          <Button
            type="text"
            size="small"
            className="action-btn"
            onClick={handleAdvancedSearchEvent}
          >
            <Space size={4}>
              <SearchOutlined />
              <span>Search & Import</span>
            </Space>
          </Button>

          <Button type="primary" onClick={handleSave}>
            <Space size={4}>
              <SaveFilled />
              <span>Save</span>
            </Space>
          </Button>

          <Button type="dashed" danger onClick={() => handleEvent(false)}>
            <Space size={4}>
              <CloseOutlined />
              <span>Cancel</span>
            </Space>
          </Button>
        </>
      );
    }

    return (
      <>
        {(userRole === "NL_ADMIN" || userRole === "NL_EDIT") && (
          <>
            {props.moduleName === "material" && !isNewRecord ? (
              <Button
                type="text"
                size="small"
                className="action-btn"
                onClick={handleGraphVisualizer}
              >
                <Space size={4}>
                  <LineChartOutlined />
                  <span>Visualizer</span>
                </Space>
              </Button>
            ) : null}

            {props.moduleName === "selectedMaterialId" && !isNewRecord ? (
              <Button
                type="text"
                size="small"
                className="action-btn"
                onClick={handleGraphVisualizer}
              >
                <Space size={4}>
                  <LineChartOutlined />
                  <span>Visualizer</span>
                </Space>
              </Button>
            ) : null}

            {!isNewRecord && (
              <>
                <Button
                  type="text"
                  size="small"
                  className="action-btn"
                  onClick={() => handleEvent(true)}
                >
                  <Space size={4}>
                    <EditOutlined />
                    <span>
                      Edit{" "}
                      {props.moduleName === "sam"
                        ? "SAM"
                        : props.moduleName}
                    </span>
                  </Space>
                </Button>

                <Button
                  type="text"
                  size="small"
                  className="action-btn"
                  danger
                  onClick={() => handleDeletEvent(true)}
                >
                  <Space size={4}>
                    <DeleteOutlined />
                    <span>
                      Delete{" "}
                      {props.moduleName === "sam"
                        ? "SAM"
                        : props.moduleName}
                    </span>
                  </Space>
                </Button>
              </>
            )}
          </>
        )}
      </>
    );
  };

  const renderStatus = () => {
    const moduleData = props.moduleFieldDataSet?.[props.moduleName];

    if (moduleData?.header !== undefined) {
      const value = moduleData.header.status;

      return (
        <Tag color={value === "In Production" ? "green" : "orange"}>
          {value}
        </Tag>
      );
    }

    return null;
  };

  const downloadPopover = () => {
    return (
      <Space>
        <Button
          onClick={() => handleDownload("json")}
          style={{ padding: 8, height: "auto" }}
        >
          <JSONFileTypeIcon style={{ fontSize: 40 }} />
        </Button>

        {props.moduleName === "material" ? (
          <Button
            onClick={() => handleDownload("xml")}
            style={{ padding: 8, height: "auto" }}
          >
            <XMLFileTypeIcon style={{ fontSize: 40 }} />
          </Button>
        ) : null}
      </Space>
    );
  };

  return (
    <div className="header-btn-grp">
      {renderStatus()}

      <Popover
        placement="bottom"
        content={downloadPopover}
        trigger="click"
        open={isPopover}
        onOpenChange={handlePopoverChange}
        arrowPointAtCenter
      >
        {["material", "sam", "fiber"].includes(props.moduleName) &&
        !isNewRecord ? (
          <Button type="text" size="small" className="action-btn">
            <Space size={4}>
              <DownloadOutlined />
              <span>Download</span>
            </Space>
          </Button>
        ) : null}
      </Popover>

      {renderActionButtons()}
    </div>
  );
}

export default ActionButtons;
